import {useNavigate} from 'react-router-dom'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {useServices} from '@/services/context'
import type {AutoAssignRuleRequest} from '@/types/auto-assign-rule'
import type {AutoAssignRuleRequestCreateRequest} from '@/types/auto-assign-rule-request'

export interface UseAutoAssignRuleRequestCreateOptions {
    onSuccess?: (request: AutoAssignRuleRequest) => void
}

/**
 * Wizard-only create mutation hook.
 *
 * Wraps {@code services.autoAssignRules.createRequest} with the wizard's
 * canonical post-submit behaviour: invalidate the rule-request list, surface
 * the success toast, and navigate to the freshly-created request's detail
 * page via the {@code routes.accessGovernance.autoAssignRuleRequests.detail}
 * helper (exact route equality, no substring concatenation).
 *
 * Distinct from {@code useAutoAssignRuleRequestMutations} on purpose: the
 * detail-page mutations (submit/approve/reject/runTest/delete) operate on an
 * existing request id, while this hook is only for the wizard's terminal POST.
 * Same-shape invalidation but different navigation target — keeping them
 * separate avoids over-stuffing the detail-page hook.
 */
export function useAutoAssignRuleRequestCreate(
    options: UseAutoAssignRuleRequestCreateOptions = {},
) {
    const services = useServices()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {onSuccess} = options

    /* eslint-disable i18next/no-literal-string -- TanStack Query keys, not user-visible strings */
    const invalidateKey = ['rule-requests'] as const
    /* eslint-enable i18next/no-literal-string */

    return useEntityMutation({
        mutationFn: (data: AutoAssignRuleRequestCreateRequest) =>
            services.autoAssignRules.createRequest(data),
        invalidate: [...invalidateKey],
        successMessage: i18n.autoAssignRuleRequests.requestCreated,
        onSuccess: (request) => {
            navigate(routes.accessGovernance.autoAssignRuleRequests.detail(request.id))
            onSuccess?.(request)
        },
    })
}
